import classNames from 'classnames'
import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'
import { Text } from './Text'

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  label?:  string
  error?:  string
  prefix?: ReactNode
  suffix?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  function Input({ label, error, prefix, suffix, disabled, className, id, ...props }, ref) {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-')

    return (
      <div className="flex flex-col gap-1.5 w-full">
        {label && (
          <Text variant="body-sm" as="label" {...({ htmlFor: inputId } as object)}>
            {label}
          </Text>
        )}
        <div
          className={classNames(
            'flex items-center gap-2 w-full',
            'bg-surface border rounded-xl px-3',
            'transition-all duration-200',
            error
              ? 'border-red-500/50'
              : 'border-surface-alt focus-within:border-secondary/60',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
        >
          {prefix && (
            <span className="shrink-0 text-text-muted">{prefix}</span>
          )}
          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            className={classNames(
              'flex-1 min-w-0 h-11 bg-transparent outline-none',
              'font-body text-base text-text-main placeholder:text-text-muted/60',
              className,
            )}
            {...props}
          />
          {suffix && (
            <span className="shrink-0 text-text-muted">{suffix}</span>
          )}
        </div>
        {error && (
          <Text variant="meta-md" tone="danger">{error}</Text>
        )}
      </div>
    )
  },
)

Input.displayName = 'Input'
